import { get, flowRight } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import classNames from 'classnames';

import { isPublished } from '@wix/communities-blog-universal/dist/src/post-utils';
import { POST_TITLE_MAXLENGTH } from '@wix/communities-blog-universal/dist/src/constants/post';
import {
  EXPERIMENT_USE_PRECOMPUTED_POST_FIELDS,
  EXPERIMENT_NEW_BM_COMPOSER_ACTION_BAR,
} from '@wix/communities-blog-experiments';
import { openPostSettings } from '../../actions/open-post-settings';
import { discardPostSettings } from '../../actions/discard-post-settings';
import withDeviceType from '../../hoc/with-device-type';
import { isOneApp } from '@wix/communities-blog-client-common/dist/src/selectors/search-params-selectors';
import { getPostSettingsValues, getPostValues } from '../../../post-settings/services/post-settings';
import InputText from '../input-text/direction-aware-input-text';
import UserAvatarImage from '../user-avatar-image/user-avatar-image';
import PostMetadataLayout from '../post-metadata-layout';
import UnpublishedChangesBadge from '../unpublished-changes-badge';
import RichContentEditor from '../../../rich-content/components/rich-content-editor';
import CharacterCount from '../character-count';
import TimeToRead from '../time-to-read';
import withTranslate from '../../hoc/with-translate';
import withFontClassName from '../../hoc/with-font-class-name';
import withAuth from '../../hoc/with-auth';
import withExperiment from '../../hoc/with-experiment';
import withIsPostSettingsEnabled from '../../hoc/with-is-post-settings-enabled';
import withFixedButtonColor from '../../hoc/with-fixed-button-color';
import {
  getPostEditorPost,
  getHasUnpublishedChanges,
  getPostEditorPostOwner,
} from '../../selectors/post-editor-selectors';
import { getPostPageEnabled } from '../../selectors/app-data-selectors';
import { getPostTitleFontSizeMobile } from '../../selectors/app-settings-selectors';
import { isPostPageBundle } from '../../services/section-route-utils';
import { isDashboard } from '../../selectors/view-mode-selectors';
import { isBusinessManager } from '../../../business-manager-old/selectors/is-business-manager-selectors';
import postFormSettings from '../../services/post-form-settings';
import getContentStateCharacterCount from '../../services/get-content-state-character-count';
import getContentStateReadingTime from '../../services/get-content-state-reading-time';
import decoratePostFormProps from '../../services/decorate-post-form-props';
import { handleEnterKeyUp } from '../../services/accessibility';
import { TAB_GENERAL } from '../../../post-settings/constants/tabs';
import PostSettingsToggle from '../../../post-settings/components/post-settings-toggle';
import styles from './post-form.scss';
import PostFormActionBar from './post-form-action-bar';

export class PostForm extends Component {
  componentWillReceiveProps({ submitting, handleSubmit }) {
    if (submitting && !this.props.submitting) {
      handleSubmit();
    }
  }

  componentWillUnmount() {
    if (this.props.isDesktop) {
      this.props.discardPostSettings();
    }
  }

  handleDrop = event => event.preventDefault();

  isCtaDisabled() {
    return this.props.submitting || this.props.invalid;
  }

  openPostSettingsModal = (activeTab, focusOnCloseId) => () => {
    const { fields, openPostSettings, owner, isPublished } = this.props;

    openPostSettings({
      activeTab,
      initialValues: getPostSettingsValues(fields, owner),
      postValues: getPostValues(fields),
      focusOnCloseId,
      isPublished,
      canPublish: !this.isCtaDisabled(),
    });
  };

  renderPostSettingsToggle = () => {
    const { isPostSettingsEnabled } = this.props;
    if (!isPostSettingsEnabled) {
      return null;
    }

    const openModal = this.openPostSettingsModal(TAB_GENERAL, PostForm.FOCUSABLE_ID_SETTINGS_TOGGLE);

    return (
      <PostSettingsToggle
        className={classNames(styles.settingsToggle, 'blog-link-hover-color', 'blog-link-hover-fill', 'blog-icon-fill')}
        onClick={openModal}
        onKeyUp={handleEnterKeyUp(openModal)}
        tabIndex="0"
        data-hook="post-form__post-settings-toggle"
        id={PostForm.FOCUSABLE_ID_SETTINGS_TOGGLE}
      />
    );
  };

  renderMetadata() {
    const { owner, fields, hasUnpublishedChanges, timeToRead, usePrecomputedPostFields } = this.props;
    const {
      content: { value },
    } = fields;
    const characterCount = getContentStateCharacterCount(value);
    return (
      <div className={styles.header}>
        <div className={styles.desktopOnly}>
          <div className={styles.metadata}>
            <UserAvatarImage user={owner} className={styles.userImage} />
            <PostMetadataLayout itemClassName={styles.metadataItem}>
              <span data-hook="post-form__owner-name">{owner.name}</span>
              {// prettier-ignore
              characterCount ? [
                  <TimeToRead key="read-time" minutes={usePrecomputedPostFields ? timeToRead : getContentStateReadingTime(value)}/>,
                  <CharacterCount key="character-count" count={characterCount}/>,
                ] : null}
            </PostMetadataLayout>
          </div>
        </div>
        <div className={styles.headerWriterContent}>
          {hasUnpublishedChanges ? (
            <div className={styles.unpublishedBadge}>
              <UnpublishedChangesBadge borderClassName={styles.unpublishedBadgeBorders} />
            </div>
          ) : null}
          {this.renderPostSettingsToggle()}
        </div>
      </div>
    );
  }

  renderEditor() {
    const {
      fields: { content },
      t,
      isOneApp,
    } = this.props;

    return (
      <div className={classNames(styles.richContentEditor)}>
        <RichContentEditor
          placeholder={t(isOneApp ? 'one-app.post-editor-placeholder' : 'text-editor.placeholder')}
          initialState={content.initialValue || undefined}
          onFocus={content.onFocus}
          onBlur={content.onBlur}
          onChange={content.onChange}
          externalModalsEnabled={this.props.externalModalsEnabled}
        />
      </div>
    );
  }

  render() {
    const {
      fields: { title },
      limit,
      postId,
      t,
      contentFontClassName,
      titleFontClassName,
      isBusinessManager,
      isMobile,
      isPostPageBundle,
      isPostPageEnabled,
      postTitleFontSizeMobile,
      onSubmit,
      onPublishButtonClick,
      onCancel,
      openTimePicker,
      isNewPostEditorActionBarEnabled,
    } = this.props;

    const { value: titleValue, onChange: onTitleChange } = title;

    const containerClassName = classNames(styles.container, 'blog-text-color', contentFontClassName);
    const contentClassName = classNames(styles.content);
    const usePostPageStyle = isPostPageBundle || (isPostPageEnabled && isBusinessManager);
    const titleInputClassName = classNames(
      styles.titleInput,
      !usePostPageStyle && styles.withFixedFontSize,
      usePostPageStyle ? 'blog-post-title-color' : 'blog-text-color',
    );
    const titleInputWrapperClassName = classNames(
      styles.titleInputWrapper,
      'blog-post-title-color',
      !usePostPageStyle && styles.withFixedWrapperFontSize,
    );
    const titleStyle = isPostPageEnabled && isMobile ? { fontSize: postTitleFontSizeMobile } : undefined;
    const showActionBar = (isBusinessManager && !isNewPostEditorActionBarEnabled) || !isBusinessManager;

    return (
      <div className={containerClassName}>
        <div className={styles.wrapper}>
          {this.renderMetadata()}
          <div className={titleInputWrapperClassName}>
            <InputText
              value={titleValue}
              onChange={onTitleChange}
              containerClassName={titleFontClassName}
              className={titleInputClassName}
              style={titleStyle}
              maxLength={limit}
              placeholder={t('post-form.give-this-post-a-title')}
              autoFocus={!postId}
              onDrop={this.handleDrop}
              data-hook="post-form__title-input"
            />
          </div>
          <div className={contentClassName} data-hook="post-form__content">
            {this.renderEditor()}
          </div>

          {showActionBar && (
            <PostFormActionBar
              onSubmit={onSubmit}
              onPublishButtonClick={onPublishButtonClick}
              onCancel={onCancel}
              openTimePicker={openTimePicker}
            />
          )}
        </div>
      </div>
    );
  }
}

PostForm.FOCUSABLE_ID_CATEGORY_TOGGLE = 'post-form__category-toggle';
PostForm.FOCUSABLE_ID_SETTINGS_TOGGLE = 'post-form__settings-toggle';

PostForm.propTypes = {
  handleSubmit: PropTypes.func,
  fields: PropTypes.object,
  submitting: PropTypes.bool,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  onPublishButtonClick: PropTypes.func.isRequired,
  titleFontClassName: PropTypes.string,
  contentFontClassName: PropTypes.string,
  limit: PropTypes.number,
  invalid: PropTypes.bool,
  isPublished: PropTypes.bool,
  t: PropTypes.func,
  postId: PropTypes.string,
  owner: PropTypes.object.isRequired,
  hasUnpublishedChanges: PropTypes.bool,
  timeToRead: PropTypes.number,
  openPostSettings: PropTypes.func.isRequired,
  isPostSettingsEnabled: PropTypes.bool,
  externalModalsEnabled: PropTypes.bool,
  discardPostSettings: PropTypes.func.isRequired,
  isDesktop: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  isOneApp: PropTypes.bool.isRequired,
  isPostPageBundle: PropTypes.bool.isRequired,
  isPostPageEnabled: PropTypes.bool.isRequired,
  isBusinessManager: PropTypes.bool.isRequired,
  useFixedButtonColor: PropTypes.bool,
  usePrecomputedPostFields: PropTypes.bool,
  postTitleFontSizeMobile: PropTypes.number,
  openTimePicker: PropTypes.func,
  isNewPostEditorActionBarEnabled: PropTypes.bool.isRequired,
};

PostForm.defaultProps = {
  limit: POST_TITLE_MAXLENGTH,
};

const mapStateToProps = state => {
  const post = getPostEditorPost(state) || {};
  return {
    postId: post._id,
    hasUnpublishedChanges: getHasUnpublishedChanges(state),
    timeToRead: get(post, 'draft.timeToRead', 1),
    isPublished: isPublished(post),
    owner: getPostEditorPostOwner(state),
    isOneApp: isOneApp(state),
    isPostPageBundle: isPostPageBundle(),
    isBusinessManager: Boolean(isDashboard(state) || isBusinessManager(state)),
    isPostPageEnabled: getPostPageEnabled(state),
    postTitleFontSizeMobile: getPostTitleFontSizeMobile(state),
  };
};

const mapDispatchToProps = {
  openPostSettings,
  discardPostSettings,
};

export default flowRight(
  reduxForm(postFormSettings),
  withAuth,
  withIsPostSettingsEnabled,
  withTranslate,
  withFontClassName,
  withDeviceType,
  withFixedButtonColor,
  withExperiment({
    usePrecomputedPostFields: EXPERIMENT_USE_PRECOMPUTED_POST_FIELDS,
    isNewPostEditorActionBarEnabled: EXPERIMENT_NEW_BM_COMPOSER_ACTION_BAR,
  }),
  connect(
    decoratePostFormProps(mapStateToProps),
    mapDispatchToProps,
  ),
)(PostForm);
